import { EventStatus, isDefined, isRemoteEvent, isLocalEvent, logNever, userIdFromAddress, streamIdFromBytes, streamIdAsString, RiverTimelineEvent, getFallbackContent, MessageType, toMembership, } from '@towns-protocol/sdk';
import { useEffect } from 'react';
import { getIsMentioned, getReactionParentId, getThreadParentId, getReplyParentId, useRawTimelineStore, } from '../../store/use-timeline-store';
import { useCallback } from 'react';
import { bin_toHexString, check } from '@towns-protocol/dlog';
import { getSpaceReviewEventDataBin } from '@towns-protocol/web3';
export function useCasablancaTimelines(casablancaClient, eventFilter) {
    const hasTimelineContent = useCallback((kind) => {
        return (kind === 'channelContent' ||
            kind === 'spaceContent' ||
            kind === 'dmChannelContent' ||
            kind === 'gdmChannelContent');
    }, []);
    const setState = useRawTimelineStore((s) => s.setState);
    useEffect(() => {
        if (!casablancaClient) {
            return;
        }
        const userId = casablancaClient.userId;
        const streamIds = new Set();
        const filterFn = (event, kind) => {
            if (isDMMessageEventBlocked(event, kind, casablancaClient)) {
                return false;
            }
            return !eventFilter || !event.content?.kind || !eventFilter.has(event.content.kind);
        };
        const onStreamInitialized = (streamId, kind) => {
            if (hasTimelineContent(kind)) {
                streamIds.add(streamId);
                const messages = casablancaClient.stream(streamId)?.view.timeline ?? [];
                const timelineEvents = messages
                    .map((event) => toEvent(event, userId))
                    .filter((event) => filterFn(event, kind));
                setState.appendEvents(timelineEvents, userId, streamId, 'initializeStream');
            }
        };
        const onStreamUpdated = (streamId, kind, change) => {
            if (hasTimelineContent(kind)) {
                const { prepended, appended, updated, confirmed } = change;
                streamIds.add(streamId);
                if (prepended) {
                    const events = prepended
                        .map((event) => toEvent(event, userId))
                        .filter((event) => filterFn(event, kind));
                    setState.prependEvents(events, userId, streamId);
                }
                if (appended) {
                    const events = appended
                        .map((event) => toEvent(event, userId))
                        .filter((event) => filterFn(event, kind));
                    setState.appendEvents(events, userId, streamId);
                }
                if (updated) {
                    const events = updated
                        .map((event) => toEvent(event, userId))
                        .filter((event) => filterFn(event, kind));
                    setState.updateEvents(events, userId, streamId);
                }
                if (confirmed) {
                    const confirmations = confirmed.map((event) => ({
                        eventId: event.hashStr,
                        confirmedInBlockNum: event.miniblockNum,
                        confirmedEventNum: event.confirmedEventNum,
                    }));
                    setState.confirmEvents(confirmations, streamId);
                }
            }
        };
        const onStreamLocalEventUpdated = (streamId, kind, localEventId, localEvent) => {
            if (hasTimelineContent(kind)) {
                streamIds.add(streamId);
                const event = toEvent(localEvent, userId);
                if (filterFn(event, kind)) {
                    setState.updateEvent(event, userId, streamId, localEventId);
                }
            }
        };
        //Initialize events that already exist in the client before the listeners started
        const timelineEvents = new Map();
        //Step 1: get all the events which are already in the river before listeners started
        casablancaClient?.streams.getStreams().forEach((stream) => {
            if (hasTimelineContent(stream.view.contentKind)) {
                streamIds.add(stream.streamId);
                timelineEvents.set(stream.streamId, []);
                console.log('$$$ useCasablancaTimelines load streamId', stream.streamId);
                stream.view.timeline.forEach((event) => {
                    const parsedEvent = toEvent(event, casablancaClient.userId);
                    if (!isDMMessageEventBlocked(parsedEvent, stream.view.contentKind, casablancaClient)) {
                        timelineEvents.get(stream.streamId)?.push(parsedEvent);
                    }
                });
            }
        });
        //Step 2: add them into the timeline
        timelineEvents.forEach((events, streamId) => {
            setState.initializeStream(userId, streamId);
            setState.appendEvents(events.filter((event) => filterFn(event, casablancaClient?.streams.get(streamId)?.view.contentKind)), userId, streamId);
        });
        casablancaClient.on('streamInitialized', onStreamInitialized);
        casablancaClient.on('streamUpdated', onStreamUpdated);
        casablancaClient.on('streamLocalEventUpdated', onStreamLocalEventUpdated);
        return () => {
            casablancaClient.off('streamInitialized', onStreamInitialized);
            casablancaClient.off('streamUpdated', onStreamUpdated);
            casablancaClient.off('streamLocalEventUpdated', onStreamLocalEventUpdated);
            setState.reset(Array.from(streamIds));
        };
    }, [casablancaClient, setState, eventFilter, hasTimelineContent]);
}
export function toEvent(timelineEvent, userId) {
    const eventId = timelineEvent.hashStr;
    const senderId = getSenderId(timelineEvent);
    const sender = {
        id: senderId,
    };
    const { content, error } = toTownsContent(timelineEvent);
    const isSender = sender.id === userId;
    const fbc = `${content?.kind ?? '??'} ${getFallbackContent(sender.id, content, error)}`;
    function extractSessionId(event) {
        const payload = event.remoteEvent?.event.payload;
        if (!payload ||
            payload.case !== 'channelPayload' ||
            payload.value.content.case !== 'message') {
            return undefined;
        }
        return payload.value.content.value.sessionIdBytes.length > 0
            ? bin_toHexString(payload.value.content.value.sessionIdBytes)
            : payload.value.content.value.sessionId;
    }
    const sessionId = extractSessionId(timelineEvent);
    return {
        eventId: eventId,
        localEventId: timelineEvent.localEvent?.localId,
        eventNum: timelineEvent.eventNum,
        latestEventId: eventId,
        latestEventNum: timelineEvent.eventNum,
        status: isSender ? getEventStatus(timelineEvent) : EventStatus.RECEIVED,
        createdAtEpochMs: Number(timelineEvent.createdAtEpochMs),
        updatedAtEpochMs: undefined,
        content: content,
        fallbackContent: fbc,
        isEncrypting: eventId.startsWith('~'),
        isLocalPending: timelineEvent.remoteEvent === undefined,
        isSendFailed: timelineEvent.localEvent?.status === 'failed',
        confirmedEventNum: timelineEvent.confirmedEventNum,
        confirmedInBlockNum: timelineEvent.miniblockNum,
        threadParentId: getThreadParentId(content),
        replyParentId: getReplyParentId(content),
        reactionParentId: getReactionParentId(content),
        isMentioned: getIsMentioned(content, userId),
        isRedacted: false, // redacted is handled in use timeline store when the redaction event is received
        sender,
        sessionId,
    };
}
function getSenderId(timelineEvent) {
    const payload = timelineEvent.remoteEvent?.event.payload;
    if (payload?.case === 'memberPayload' &&
        payload?.value.content.case === 'memberBlockchainTransaction') {
        return userIdFromAddress(payload.value.content.value.fromUserAddress);
    }
    return timelineEvent.creatorUserId;
}
function toTownsContent(timelineEvent) {
    if (isLocalEvent(timelineEvent)) {
        return toTownsContent_FromChannelMessage(timelineEvent.localEvent.channelMessage, 'local event', timelineEvent.hashStr);
    }
    else if (isRemoteEvent(timelineEvent)) {
        return toTownsContent_fromParsedEvent(timelineEvent.hashStr, timelineEvent);
    }
    else {
        return { error: 'unknown event content type' };
    }
}
function validateEvent(eventId, message) {
    let error;
    if (!message.event.payload || !message.event.payload.value) {
        error = { error: 'payloadless payload' };
        return error;
    }
    if (!message.event.payload.case) {
        error = { error: 'caseless payload' };
        return error;
    }
    if (!message.event.payload.value.content.case) {
        error = { error: `${message.event.payload.case} - caseless payload content` };
        return error;
    }
    const description = `${message.event.payload.case}::${message.event.payload.value.content.case} id: ${eventId}`;
    return { description };
}
function toTownsContent_fromParsedEvent(eventId, timelineEvent) {
    const message = timelineEvent.remoteEvent;
    const { error, description } = validateEvent(eventId, message);
    if (error) {
        return { error };
    }
    if (!description) {
        return { error: 'no description' };
    }
    switch (message.event.payload.case) {
        case 'userPayload':
            return toTownsContent_UserPayload(timelineEvent, message, message.event.payload.value, description);
        case 'channelPayload':
            return toTownsContent_ChannelPayload(eventId, timelineEvent, message.event.payload.value, description);
        case 'dmChannelPayload':
            return toTownsContent_ChannelPayload(eventId, timelineEvent, message.event.payload.value, description);
        case 'gdmChannelPayload':
            return toTownsContent_ChannelPayload(eventId, timelineEvent, message.event.payload.value, description);
        case 'spacePayload':
            return toTownsContent_SpacePayload(eventId, message, message.event.payload.value, description);
        case 'userMetadataPayload':
            return {
                error: `${description} userMetadataPayload not supported?`,
            };
        case 'userSettingsPayload':
            return {
                error: `${description} userSettingsPayload not supported?`,
            };
        case 'userInboxPayload':
            return {
                error: `${description} userInboxPayload not supported?`,
            };
        case 'miniblockHeader':
            return toTownsContent_MiniblockHeader(eventId, message, message.event.payload.value, description);
        case 'mediaPayload':
            return {
                error: `${description} mediaPayload not supported?`,
            };
        case 'memberPayload':
            return toTownsContent_MemberPayload(timelineEvent, message, message.event.payload.value, description);
        case undefined:
            return { error: `Undefined payload case: ${description}` };
        default:
            logNever(message.event.payload);
            return { error: `Unknown payload case: ${description}` };
    }
}
function toTownsContent_MiniblockHeader(eventId, message, value, _description) {
    return {
        content: {
            kind: RiverTimelineEvent.MiniblockHeader,
            miniblockNum: value.miniblockNum,
            hasSnapshot: value.snapshot !== undefined,
        },
    };
}
function toTownsContent_MemberPayload(event, message, value, description) {
    switch (value.content.case) {
        case 'membership':
            return {
                content: {
                    kind: RiverTimelineEvent.StreamMembership,
                    userId: userIdFromAddress(value.content.value.userAddress),
                    initiatorId: userIdFromAddress(value.content.value.initiatorAddress),
                    membership: toMembership(value.content.value.op),
                },
            };
        case 'keySolicitation':
            return {
                content: {
                    kind: RiverTimelineEvent.KeySolicitation,
                    sessionIds: value.content.value.sessionIds,
                    deviceKey: value.content.value.deviceKey,
                    isNewDevice: value.content.value.isNewDevice,
                },
            };
        case 'keyFulfillment':
            return {
                content: {
                    kind: RiverTimelineEvent.Fulfillment,
                    sessionIds: value.content.value.sessionIds,
                    deviceKey: value.content.value.deviceKey,
                    to: userIdFromAddress(value.content.value.userAddress),
                    from: message.creatorUserId,
                },
            };
        case 'displayName':
            return {
                content: {
                    kind: RiverTimelineEvent.SpaceDisplayName,
                    userId: message.creatorUserId,
                    displayName: event.decryptedContent?.kind === 'text'
                        ? event.decryptedContent.content
                        : value.content.value.ciphertext,
                },
            };
        case 'username':
            return {
                content: {
                    kind: RiverTimelineEvent.SpaceUsername,
                    userId: message.creatorUserId,
                    username: event.decryptedContent?.kind === 'text'
                        ? event.decryptedContent.content
                        : value.content.value.ciphertext,
                },
            };
        case 'ensAddress':
            return {
                content: {
                    kind: RiverTimelineEvent.SpaceEnsAddress,
                    userId: message.creatorUserId,
                    ensAddress: value.content.value,
                },
            };
        case 'nft':
            return {
                content: {
                    kind: RiverTimelineEvent.SpaceNft,
                    userId: message.creatorUserId,
                    contractAddress: bin_toHexString(value.content.value.contractAddress),
                    tokenId: bin_toHexString(value.content.value.tokenId),
                    chainId: value.content.value.chainId,
                },
            };
        case 'pin':
            return {
                content: {
                    kind: RiverTimelineEvent.Pin,
                    userId: message.creatorUserId,
                    pinnedEventId: bin_toHexString(value.content.value.eventId),
                },
            };
        case 'unpin':
            return {
                content: {
                    kind: RiverTimelineEvent.Unpin,
                    userId: message.creatorUserId,
                    unpinnedEventId: bin_toHexString(value.content.value.eventId),
                },
            };
        case 'memberBlockchainTransaction': {
            const fromUserAddress = value.content.value.fromUserAddress;
            const transaction = value.content.value.transaction;
            if (!transaction) {
                return { error: `${description} no transaction` };
            }
            switch (transaction.content.case) {
                case 'tip': {
                    if (!transaction.receipt?.transactionHash) {
                        return { error: `${description} no transactionHash` };
                    }
                    const tipContent = transaction.content.value;
                    if (!tipContent.event) {
                        return { error: `${description} no event in tip` };
                    }
                    return {
                        content: {
                            kind: RiverTimelineEvent.TipEvent,
                            transaction: transaction,
                            tip: tipContent,
                            transactionHash: bin_toHexString(transaction.receipt.transactionHash),
                            fromUserId: userIdFromAddress(fromUserAddress),
                            refEventId: bin_toHexString(tipContent.event.messageId),
                            toUserId: userIdFromAddress(tipContent.toUserAddress),
                        },
                    };
                }
                case 'spaceReview': {
                    if (!transaction.receipt) {
                        return { error: `${description} no receipt` };
                    }
                    const reviewContent = transaction.content.value;
                    if (!reviewContent.event) {
                        return { error: `${description} no event in space review` };
                    }
                    const { comment, rating } = getSpaceReviewEventDataBin(transaction.receipt.logs, reviewContent.event.user);
                    return {
                        content: {
                            kind: RiverTimelineEvent.SpaceReview,
                            action: reviewContent.action,
                            rating: rating,
                            comment: comment,
                            fromUserId: userIdFromAddress(fromUserAddress),
                        },
                    };
                }
                case 'tokenTransfer': {
                    const transferContent = transaction.content.value;
                    return {
                        content: {
                            kind: RiverTimelineEvent.TokenTransfer,
                            transaction: transaction,
                            transfer: transferContent,
                            fromUserId: userIdFromAddress(fromUserAddress),
                            createdAtEpochMs: event.createdAtEpochMs,
                            threadParentId: bin_toHexString(transferContent.messageId),
                        },
                    };
                }
                case undefined:
                    return {
                        content: {
                            kind: RiverTimelineEvent.MemberBlockchainTransaction,
                            transaction: value.content.value.transaction,
                            fromUserId: bin_toHexString(value.content.value.fromUserAddress),
                        },
                    };
                default:
                    logNever(transaction.content);
                    return { error: `${description} unknown transaction content` };
            }
        }
        case 'encryptionAlgorithm':
            return {
                error: `Encryption Algorithm not supported: ${description}`,
            };
        case undefined:
            return { error: `Undefined payload case: ${description}` };
        default:
            logNever(value.content);
            return {
                error: `Unknown payload case: ${description}`,
            };
    }
}
function toTownsContent_UserPayload(event, message, value, description) {
    switch (value.content.case) {
        case 'inception': {
            return {
                content: {
                    kind: RiverTimelineEvent.Inception,
                    creatorId: message.creatorUserId,
                    type: message.event.payload.case,
                },
            };
        }
        case 'userMembership': {
            const payload = value.content.value;
            const streamId = streamIdFromBytes(payload.streamId);
            return {
                content: {
                    kind: RiverTimelineEvent.StreamMembership,
                    userId: '', // this is just the current user
                    initiatorId: '',
                    membership: toMembership(payload.op),
                    streamId: streamId,
                },
            };
        }
        case 'userMembershipAction': {
            // these are admin actions where you can invite, join, or kick someone
            const payload = value.content.value;
            return {
                content: {
                    kind: RiverTimelineEvent.StreamMembership,
                    userId: userIdFromAddress(payload.userId),
                    initiatorId: event.remoteEvent.creatorUserId,
                    membership: toMembership(payload.op),
                },
            };
        }
        case 'blockchainTransaction': {
            return {
                content: {
                    kind: RiverTimelineEvent.UserBlockchainTransaction,
                    transaction: value.content.value,
                },
            };
        }
        case 'receivedBlockchainTransaction': {
            return {
                content: {
                    kind: RiverTimelineEvent.UserReceivedBlockchainTransaction,
                    receivedTransaction: value.content.value,
                },
            };
        }
        case undefined: {
            return { error: `Undefined payload case: ${description}` };
        }
        default: {
            logNever(value.content);
            return {
                error: `Unknown payload case: ${description}`,
            };
        }
    }
}
function toTownsContent_ChannelPayload(eventId, timelineEvent, value, description) {
    const message = timelineEvent.remoteEvent;
    switch (value.content.case) {
        case 'inception': {
            return {
                content: {
                    kind: RiverTimelineEvent.Inception,
                    creatorId: message.creatorUserId,
                    type: message.event.payload.case,
                },
            };
        }
        case 'message': {
            if (timelineEvent.decryptedContent?.kind === 'channelMessage') {
                return toTownsContent_FromChannelMessage(timelineEvent.decryptedContent.content, description, eventId);
            }
            const payload = value.content.value;
            return toTownsContent_ChannelPayload_Message(timelineEvent, payload, description);
        }
        case 'channelProperties': {
            const payload = value.content.value;
            return toTownsContent_ChannelPayload_ChannelProperties(timelineEvent, payload, description);
        }
        case 'redaction':
            return {
                content: {
                    kind: RiverTimelineEvent.RedactionActionEvent,
                    refEventId: bin_toHexString(value.content.value.eventId),
                    adminRedaction: true,
                },
            };
        case undefined: {
            return { error: `Undefined payload case: ${description}` };
        }
        default:
            logNever(value.content);
            return { error: `Unknown payload case: ${description}` };
    }
}
function toTownsContent_FromChannelMessage(channelMessage, description, eventId) {
    switch (channelMessage.payload?.case) {
        case 'post':
            return (toTownsContent_ChannelPayload_Message_Post(channelMessage.payload.value, eventId, undefined, description) ?? {
                error: `${description} unknown message type`,
            });
        case 'reaction':
            return {
                content: {
                    kind: RiverTimelineEvent.Reaction,
                    reaction: channelMessage.payload.value.reaction,
                    targetEventId: channelMessage.payload.value.refEventId,
                },
            };
        case 'redaction':
            return {
                content: {
                    kind: RiverTimelineEvent.RedactionActionEvent,
                    refEventId: channelMessage.payload.value.refEventId,
                    adminRedaction: false,
                },
            };
        case 'edit': {
            const newPost = channelMessage.payload.value.post;
            if (!newPost) {
                return { error: `${description} no post in edit` };
            }
            const newContent = toTownsContent_ChannelPayload_Message_Post(newPost, eventId, channelMessage.payload.value.refEventId, description);
            return newContent ?? { error: `${description} no content in edit` };
        }
        case undefined:
            return { error: `Undefined payload case: ${description}` };
        default: {
            logNever(channelMessage.payload);
            return {
                error: `Unknown payload case: ${description}`,
            };
        }
    }
}
function toTownsContent_ChannelPayload_Message(timelineEvent, payload, _description) {
    if (payload.refEventId) {
        return {
            content: {
                kind: RiverTimelineEvent.ChannelMessageEncryptedWithRef,
                refEventId: payload.refEventId,
            },
        };
    }
    return {
        // if payload is an EncryptedData message, than it is encrypted content kind
        content: {
            kind: RiverTimelineEvent.ChannelMessageEncrypted,
            error: timelineEvent.decryptedContentError,
        },
    };
}
function toTownsContent_ChannelPayload_ChannelProperties(timelineEvent, payload, description) {
    if (timelineEvent.decryptedContent?.kind === 'channelProperties') {
        return {
            content: {
                kind: RiverTimelineEvent.ChannelProperties,
                properties: timelineEvent.decryptedContent.content,
            },
        };
    }
    // If the payload is encrypted, we display nothing.
    return { error: `${description} encrypted channel properties` };
}
function toTownsContent_ChannelPayload_Message_Post(value, eventId, editsEventId, description) {
    switch (value.content.case) {
        case 'text':
            return {
                content: {
                    kind: RiverTimelineEvent.ChannelMessage,
                    body: value.content.value.body,
                    threadId: value.threadId,
                    threadPreview: value.threadPreview,
                    replyId: value.replyId,
                    replyPreview: value.replyPreview,
                    mentions: value.content.value.mentions,
                    editsEventId: editsEventId,
                    content: {
                        msgType: MessageType.Text,
                    },
                    attachments: toAttachments(value.content.value.attachments, eventId),
                },
            };
        case 'image':
            return {
                content: {
                    kind: RiverTimelineEvent.ChannelMessage,
                    body: value.content.value.title,
                    threadId: value.threadId,
                    threadPreview: value.threadPreview,
                    mentions: [],
                    editsEventId: editsEventId,
                    content: {
                        msgType: MessageType.Image,
                        info: value.content.value.info,
                        thumbnail: value.content.value.thumbnail,
                    },
                },
            };
        case 'gm':
            return {
                content: {
                    kind: RiverTimelineEvent.ChannelMessage,
                    body: value.content.value.typeUrl,
                    threadId: value.threadId,
                    threadPreview: value.threadPreview,
                    mentions: [],
                    editsEventId: editsEventId,
                    content: {
                        msgType: MessageType.GM,
                        data: value.content.value.value,
                    },
                },
            };
        case undefined:
            return { error: `Undefined payload case: ${description}` };
        default:
            logNever(value.content);
            return { error: `Unknown payload case: ${description}` };
    }
}
function toTownsContent_SpacePayload(eventId, message, value, description) {
    switch (value.content.case) {
        case 'inception': {
            return {
                content: {
                    kind: RiverTimelineEvent.Inception,
                    creatorId: message.creatorUserId,
                    type: message.event.payload.case,
                },
            };
        }
        case 'channel': {
            const payload = value.content.value;
            const childId = streamIdAsString(payload.channelId);
            return {
                content: {
                    kind: RiverTimelineEvent.ChannelCreate,
                    creatorId: message.creatorUserId,
                    channelId: childId,
                    channelOp: payload.op,
                    channelSettings: payload.settings,
                },
            };
        }
        case 'updateChannelAutojoin': {
            const payload = value.content.value;
            const channelId = streamIdAsString(payload.channelId);
            return {
                content: {
                    kind: RiverTimelineEvent.SpaceUpdateAutojoin,
                    autojoin: payload.autojoin,
                    channelId: channelId,
                },
            };
        }
        case 'updateChannelHideUserJoinLeaveEvents': {
            const payload = value.content.value;
            const channelId = streamIdAsString(payload.channelId);
            return {
                content: {
                    kind: RiverTimelineEvent.SpaceUpdateHideUserJoinLeaves,
                    hideUserJoinLeaves: payload.hideUserJoinLeaveEvents,
                    channelId: channelId,
                },
            };
        }
        case 'spaceImage': {
            return {
                content: {
                    kind: RiverTimelineEvent.SpaceImage,
                },
            };
        }
        case undefined:
            return { error: `Undefined payload case: ${description}` };
        default:
            logNever(value.content);
            return { error: `Unknown payload case: ${description}` };
    }
}
function getEventStatus(timelineEvent) {
    if (timelineEvent.remoteEvent) {
        return EventStatus.SENT;
    }
    else if (timelineEvent.localEvent && timelineEvent.hashStr.startsWith('~')) {
        return EventStatus.ENCRYPTING;
    }
    else if (timelineEvent.localEvent) {
        switch (timelineEvent.localEvent.status) {
            case 'failed':
                return EventStatus.NOT_SENT;
            case 'sending':
                return EventStatus.SENDING;
            case 'sent':
                return EventStatus.SENT;
            default:
                logNever(timelineEvent.localEvent.status);
                return EventStatus.NOT_SENT;
        }
    }
    else {
        console.error('$$$ useCasablancaTimelines unknown event status', { timelineEvent });
        return EventStatus.NOT_SENT;
    }
}
function toAttachments(attachments, parentEventId) {
    return attachments
        .map((attachment, index) => toAttachment(attachment, parentEventId, index))
        .filter(isDefined);
}
function toAttachment(attachment, parentEventId, index) {
    const id = `${parentEventId}-${index}`;
    switch (attachment.content.case) {
        case 'chunkedMedia': {
            const info = attachment.content.value.info;
            if (!info) {
                console.error('$$$ useCasablancaTimelines invalid chunkedMedia attachment', {
                    attachment,
                });
                return undefined;
            }
            const thumbnailInfo = attachment.content.value.thumbnail?.info;
            const thumbnailContent = attachment.content.value.thumbnail?.content;
            const thumbnail = thumbnailInfo && thumbnailContent
                ? {
                    info: thumbnailInfo,
                    content: thumbnailContent,
                }
                : undefined;
            const encryption = attachment.content.value.encryption.case === 'aesgcm'
                ? attachment.content.value.encryption.value
                : undefined;
            if (!encryption) {
                console.error('$$$ useCasablancaTimelines invalid chunkedMedia encryption', {
                    attachment,
                });
                return undefined;
            }
            return {
                type: 'chunked_media',
                info,
                streamId: attachment.content.value.streamId,
                encryption,
                id,
                thumbnail: thumbnail,
            };
        }
        case 'embeddedMedia': {
            const info = attachment.content.value.info;
            if (!info) {
                return undefined;
            }
            return {
                type: 'embedded_media',
                info,
                content: attachment.content.value.content,
                id,
            };
        }
        case 'image': {
            const info = attachment.content.value.info;
            if (!info) {
                return undefined;
            }
            return { type: 'image', info, id };
        }
        case 'embeddedMessage': {
            const content = attachment.content.value;
            if (!content?.post || !content?.info) {
                return undefined;
            }
            const channelMessageEvent = toTownsContent_ChannelPayload_Message_Post(content.post, content.info.messageId, undefined, '').content;
            return channelMessageEvent?.kind === RiverTimelineEvent.ChannelMessage
                ? {
                    type: 'embedded_message',
                    ...content,
                    info: content.info,
                    channelMessageEvent: channelMessageEvent,
                    id,
                }
                : undefined;
        }
        case 'unfurledUrl': {
            const content = attachment.content.value;
            return {
                type: 'unfurled_link',
                url: content.url,
                title: content.title,
                description: content.description,
                image: content.image,
                id,
            };
        }
        case 'ticker': {
            const content = attachment.content.value;
            return {
                id,
                type: 'ticker',
                address: content.address,
                chainId: content.chainId,
            };
        }
        default:
            return undefined;
    }
}
function isDMMessageEventBlocked(event, kind, casablancaClient) {
    if (kind !== 'dmChannelContent') {
        return false;
    }
    if (!casablancaClient?.userSettingsStreamId) {
        return false;
    }
    const stream = casablancaClient.stream(casablancaClient.userSettingsStreamId);
    check(isDefined(stream), 'stream must be defined');
    return stream.view.userSettingsContent.isUserBlockedAt(event.sender.id, event.eventNum);
}
//# sourceMappingURL=useCasablancaTimelines.js.map